import C from "@/components/sidebar/StandardSidebar.module.css";

import { localeCompareBy } from "@/components/data/comparers.ts";
import { getFilteredItems } from "@/components/data/getFilteredItems.ts";
import { getSortedItems, type SimpleSortField } from "@/components/data/getSortedItems.ts";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { Gb } from "@/components/l10n/Gb.tsx";
import { EmptyItem } from "@/components/sidebar/EmptyItem.tsx";
import { PriceBlock } from "@/components/sidebar/PriceBlock.tsx";
import { ListResult, SidebarList, type SidebarListState } from "@/components/sidebar/SidebarList.tsx";
import { StandardBlock } from "@/components/sidebar/StandardSidebar.tsx";
import { ResourceCreateLinks } from "@/modules/api/resourceCreateLinks.ts";
import type { StorageAccountWithPrice } from "@/modules/storage/account/storageAccountApi.ts";
import { storageAccountListQueryWithPricesAtom } from "@/modules/storage/account/storageAccountQuery.ts";
import type { BucketWithPrice } from "@/modules/storage/bucket/bucketApi";
import { storageAccountLink } from "@/modules/storage/links.ts";
import { cn } from "@/utils/classNames.ts";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { useAtomValue } from "jotai/react";

const sortFields = [
    {
        id: "name",
        title: <Trans>Name</Trans>,
        compare: localeCompareBy((i) => getStorageItemName(i)),
    },
    { id: "size_bytes", title: <Trans>Size</Trans>, getValue: (i) => i.size_bytes },
    { id: "price", title: <Trans>Monthly cost</Trans>, getValue: (i) => i.price.hourly },
] satisfies SimpleSortField<BucketWithPrice | StorageAccountWithPrice>[];

function getStorageItemName(item: BucketWithPrice | StorageAccountWithPrice) {
    return item.$type === "storage_account" ? item.title : item.name;
}

export default function StorageList() {
    const { t } = useLingui();
    return (
        <SidebarList
            data={(props) => <StorageData {...props} />}
            title={t`Storage`}
            sortFields={sortFields}
            actionButton={{
                title: t`Create new`,
                action: ResourceCreateLinks.storage_account,
            }}
            listId="storage"
        />
    );
}

export function StorageData({ queryString, sortId, sortAsc }: SidebarListState) {
    // const query = useAtomValue(storageListQueryWithPricesAtom);
    const accountsQuery = useAtomValue(storageAccountListQueryWithPricesAtom);
    // const { data } = query;
    const { data: accountsData } = accountsQuery;

    // let storageItems = getFilteredItems(queryString, [...(data?.values() ?? [])], (item) => [item.name]);
    // storageItems = getSortedItems(sortFields, { sortId, sortAsc }, storageItems);

    let storageAccountItems = getFilteredItems(queryString, [...(accountsData?.values() ?? [])], (item) => [
        item.title,
    ]);
    storageAccountItems = getSortedItems(sortFields, { sortId, sortAsc }, storageAccountItems);

    return (
        <ListResult
            items={[
                ...storageAccountItems.map((obj) => <StorageAccountBlock key={obj.uuid} item={obj} />),
                //...storageItems.map((obj) => <StorageBlock key={obj.name} item={obj} />),
            ]}
            isSearching={!!queryString}
            query={accountsQuery}
            emptyItem={
                <EmptyItem icon="jp-stack-icon">
                    <Trans>Create a new storage account, create some buckets and start storing your data.</Trans>
                </EmptyItem>
            }
        />
    );
}

export function StorageAccountBlock({ item }: { item: StorageAccountWithPrice }) {
    const { title, billing_account_id, size_bytes, number_of_buckets } = item;

    return (
        <StandardBlock link={storageAccountLink(item)}>
            <div className={C.StandardBlockIcon}>
                <MaskIcon className="jp-archive-icon size-1rem" />
            </div>

            <div className={cn(C.StandardBlockName, "text-ellipsis")}>
                <b className="text-ellipsis">{title}</b>
                <div className="text-ellipsis text-primary">
                    {number_of_buckets} <Plural value={number_of_buckets} one="bucket" other="buckets" />
                </div>
            </div>

            <div className={C.StandardBlockType}>
                <div className="text-primary">
                    <Gb bytes={size_bytes} />
                </div>
            </div>

            <PriceBlock price={item.price} billingAccountId={billing_account_id} />
        </StandardBlock>
    );
}

/* export function StorageBlock({ item }: { item: StorageWithPrice }) {
    const { size_bytes, name, num_objects, billing_account_id } = item;

    return (
        <StandardBlock link={objectStorageLink(item)}>
            <div className={C.StandardBlockIcon}>
                <MaskIcon className="jp-stack-icon size-1rem" />
            </div>

            <div className={cn(C.StandardBlockName, "text-ellipsis")}>
                <b className="text-ellipsis">{name}</b>
                <div className="text-ellipsis text-primary">
                    <Trans>
                        {num_objects} <Plural value={num_objects} one="object" other="objects" />
                    </Trans>
                </div>
            </div>

            <div className={C.StandardBlockType}>
                <div className="text-primary">
                    <Gb bytes={size_bytes} />
                </div>
            </div>

            <PriceBlock price={item.price} billingAccountId={billing_account_id} />
        </StandardBlock>
    );
} */
